// @flow
import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import i18n from "i18next";
import k from "src/i18n/keys";

import { useDebouncedCallback } from "use-debounce";

import { getUserNames } from "src/reducers";
import TextInput from "src/components/Unifize/Input/TextInput";
import { setChatroomNotificationUserFilter } from "src/actions/chatroom";
import Checkbox from "src/components/Unifize/Checkbox";
import { MenuItem } from "src/components/Unifize/Menu/MenuItem";

import type { UID, UserNames, Action } from "src/types";

import FilterSubmenu from "./FilterSubmenu";
import {
  CheckboxesContainer as UsersContainer,
  UserFilterMenu as StyledUserFilterMenu,
} from "./styles";

type Props = {
  dispatch: (action: Action, payload: any) => void,
  participants: Array<UID>,
  filteredUsers: Set<UID>,
  automatorUIDs: Set<UID>,
  menuRef: React$ElementRef<any>,
};

const UserFilterMenu = ({
  dispatch,
  participants,
  filteredUsers,
  automatorUIDs,
  menuRef,
}: Props) => {
  const allUserNames: UserNames = useSelector(({ app }) => getUserNames(app));
  const userParticipants = useMemo(
    () =>
      automatorUIDs.size
        ? participants.filter((user) => !automatorUIDs.has(user))
        : participants,
    [automatorUIDs, participants],
  );
  const [users, setUsers] = useState(userParticipants);
  const [searchQuery, setSearchQuery] = useState("");

  const selectedUsers = useMemo(
    () => participants.filter((user) => !filteredUsers.has(user)),
    [filteredUsers, participants],
  );

  const addUserToNotificationFilter = (user: UID) => {
    if (!filteredUsers.has(user)) {
      dispatch(setChatroomNotificationUserFilter([...filteredUsers, user]));
    }
  };

  const removeUserFromNotificationFilter = (user: UID) => {
    if (filteredUsers.has(user)) {
      const newFilteredUsers = new Set(filteredUsers);
      newFilteredUsers.delete(user);
      dispatch(setChatroomNotificationUserFilter(Array.from(newFilteredUsers)));
    }
  };

  const handleSelectDeselectAll = (checked: boolean) => {
    if (checked) {
      const newFilteredUsers = new Set(filteredUsers);
      newFilteredUsers.forEach((user) => {
        if (participants.includes(user)) {
          newFilteredUsers.delete(user);
        }
      });
      dispatch(setChatroomNotificationUserFilter(Array.from(newFilteredUsers)));
    } else {
      dispatch(setChatroomNotificationUserFilter(participants));
    }
  };

  const searchUsers = (query: string) => {
    if (query) {
      setUsers(
        participants.filter((participant) =>
          // $FlowFixMe
          allUserNames[participant]?.displayName
            .toLowerCase()
            .includes(query.toLowerCase()),
        ),
      );
    } else if (query?.length === 0 && users.length !== participants.length) {
      setUsers(participants);
    }
  };

  const [debouncedSearchUsers] = useDebouncedCallback(searchUsers, 400);

  return (
    <FilterSubmenu menuRef={menuRef}>
      <StyledUserFilterMenu>
        <TextInput
          value={searchQuery}
          type="text"
          placeholder={i18n.t(k.SEARCH)}
          onChange={(e: SyntheticInputEvent<HTMLInputElement>) => {
            setSearchQuery(e.target.value);
            debouncedSearchUsers(e.target.value);
          }}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
        />
        <UsersContainer>
          {userParticipants.length > 1 && (
            <MenuItem
              padding="0 0.5rem"
              height="xl"
              hoverColor="rgba(133, 133, 133, 0.05)"
              display="block"
            >
              <Checkbox
                label={i18n.t(k.ALL)}
                checked={selectedUsers.length === participants.length}
                onChange={handleSelectDeselectAll}
                indeterminate={
                  selectedUsers.length > 0 &&
                  selectedUsers.length < participants.length
                }
                labelFullWidth
                height="100%"
              />
            </MenuItem>
          )}
          {users.map((user, index) => (
            <MenuItem
              key={`userFilterMenu_${index}_${user}`}
              padding="0 0.5rem"
              height="xl"
              hoverColor="rgba(133, 133, 133, 0.05)"
              display="block"
            >
              <Checkbox
                label={allUserNames[user]?.displayName ?? "Unknown"}
                checked={!filteredUsers.has(user)}
                onChange={(checked) => {
                  if (!checked) {
                    addUserToNotificationFilter(user);
                  } else {
                    removeUserFromNotificationFilter(user);
                  }
                }}
                labelFullWidth
                height="100%"
              />
            </MenuItem>
          ))}
        </UsersContainer>
      </StyledUserFilterMenu>
    </FilterSubmenu>
  );
};

export default UserFilterMenu;
